/**
 * Incobrables por fechas — préstamos con la deuda castigada (status 'I').
 *
 * Filtra por la FECHA DEL CASTIGO (prestamo.fincobrable, la estampa el
 * botón "Marcar incobrable" del expediente). Los que el escritorio dejó en
 * 'I' no tienen fecha: salen solo con "Incluir sin fecha". Mismo programa
 * que Cancelados por fechas. Exporta a Excel e imprime.
 */
import React, { useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioAnioISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales });
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

const CABECERAS = [
  "Préstamo",
  "Inicio",
  "Cédula",
  "Nombres",
  "Solicitado",
  "Pagado",
  "Pendiente",
  "Incobrable",
  "Motivo",
  "Usuario",
  "Sucursal",
];

export default function IncobrablesPorFechas() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioAnioISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [cliente, setCliente] = useState("");
  const [prestamosPersonales, setPrestamosPersonales] = useState(false);
  const [incluirSinFecha, setIncluirSinFecha] = useState(false);

  const [prestamos, setPrestamos] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/incobrablesporfechas", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        cliente: cliente.trim(),
        prestamospersonales: prestamosPersonales,
        incluirsinfecha: incluirSinFecha,
      });
      if (ok) {
        setPrestamos(data.prestamos || []);
        setTotales(data.totales || null);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitulo =
    `Marcados incobrables del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}` +
    `${incluirSinFecha ? " (y sin fecha)" : ""}${cliente ? ` · ${cliente}` : ""}`;

  const fila = (p, fmt) => [
    p.noprest,
    p.fecha,
    p.cedula,
    p.nombres,
    fmt(p.cantsol),
    fmt(p.pagado),
    fmt(p.pendiente),
    p.fincobrable || "Sin fecha",
    p.motivo,
    p.usuarioincobrable,
    p.sucursal,
  ];

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Incobrables por fechas"],
      [subtitulo],
      [],
      CABECERAS,
      ...prestamos.map((p) => fila(p, (v) => v)),
      [],
      [
        "",
        "",
        "",
        "TOTAL",
        totales?.monto || 0,
        totales?.pagado || 0,
        totales?.pendiente || 0,
        "",
        "",
        "",
        "",
      ],
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Incobrables");
    XLSX.writeFile(libro, `Incobrables_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    imprimirListado(
      "Incobrables por fechas",
      subtitulo,
      CABECERAS,
      prestamos.map((p) => fila(p, fmtMoney)),
      [
        "",
        "",
        "",
        "TOTAL",
        fmtMoney(totales?.monto),
        fmtMoney(totales?.pagado),
        fmtMoney(totales?.pendiente),
        "",
        "",
        "",
        "",
      ]
    );
  };

  const columnas = [
    { field: "noprest", headerName: "Préstamo", width: 90 },
    { field: "fecha", headerName: "Inicio", width: 100 },
    { field: "cedula", headerName: "Cédula", width: 120 },
    { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 180 },
    moneyCol("cantsol", "Solicitado"),
    moneyCol("pagado", "Pagado"),
    moneyCol("pendiente", "Pendiente"),
    {
      field: "fincobrable",
      headerName: "Incobrable",
      width: 105,
      valueFormatter: (value) => value || "Sin fecha",
    },
    { field: "motivo", headerName: "Motivo", flex: 1, minWidth: 160 },
    { field: "usuarioincobrable", headerName: "Usuario", width: 110 },
    { field: "sucursal", headerName: "Sucursal", width: 100 },
  ];

  const hayDatos = prestamos.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Incobrables por fechas"
        subtitle="Préstamos con la deuda castigada, por la fecha en que se marcaron incobrables"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Cliente (nombre o cédula, opcional)"
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo de préstamo"
                value={prestamosPersonales ? "personal" : "general"}
                onChange={(e) => setPrestamosPersonales(e.target.value === "personal")}
                sx={fpInputSx}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="general">General</option>
                <option value="personal">Personal</option>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            {/* Los marcados desde el escritorio ares no tienen fecha de castigo. */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirSinFecha}
                    onChange={(e) => setIncluirSinFecha(e.target.checked)}
                    size="small"
                  />
                }
                label="Incluir los incobrables sin fecha (marcados desde el sistema anterior)"
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="CANTIDAD" value={totales?.cantidad ?? 0} />
            <ResumenChip label="MONTO" value={`RD$ ${fmtMoney(totales?.monto)}`} />
            <ResumenChip
              label="PAGADO"
              value={`RD$ ${fmtMoney(totales?.pagado)}`}
              color="#2E7D32"
            />
            <ResumenChip
              label="PENDIENTE CASTIGADO"
              value={`RD$ ${fmtMoney(totales?.pendiente)}`}
              color={FP.color.danger}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={prestamos}
              columns={columnas}
              getRowId={(r) => r.noprest}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
